import React from 'react';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { ContactFormProps, FormFields } from '.';
import Button from '../Button';
import CardsDisplay from '../CardsDisplay';
import { Input, TextArea } from '../Input';
import type { PhoneInputProps } from 'react-phone-input-2';

const Phone = dynamic<PhoneInputProps>(() => import('../Input/phone'), { ssr: false });
import { validatePhone, formatPhoneBR } from '../../../utils/string';
import { LoadingIcon } from '../LoadingIcon';
import { ContactFormWrapper, FormErrorMessage, LookingForJobText } from './styles';

interface ContactFormTemplateProps extends ContactFormProps {
  onSubmit: (data: FormFields, newChecked?: boolean) => void;
  loading: boolean;
  locale: string;
  error: boolean | string;
  sitekey: string;
  success: boolean;
  setError: (value: boolean) => void;
  isLookingForJob: boolean;
  successCardVariant?: 'default' | 'alternative';
  successMessageOverride?: string;
}

const texts = {
  pt: {
    name: 'Nome',
    email: 'E-mail',
    phone: 'Telefone',
    message: 'Mensagem',
    companyName: 'Nome da empresa',
    companySegment: 'Segmento da empresa',
    role: 'Seu cargo',
    send: 'Enviar',
    error: {
      default: 'Ocorreu um erro ao enviar o formulário',
      captcha:
        'Você foi identificado como um robô. Se não for o caso, entre em contato conosco de outras maneiras',
    },
    success: {
      title: 'Mensagem recebida!',
      message: 'Em breve entraremos em contato',
    },
    validation: {
      phone: {
        min: 'Telefone deve conter no mínimo 7 dígitos',
        max: 'Telefone deve conter no máximo 20 dígitos',
      },
    },
  },
  en: {
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    message: 'Message',
    companyName: 'Company name',
    companySegment: 'Company segment',
    role: 'Your role',
    send: 'Send',
    error: {
      default: 'An error occurred on submitting your answers',
      captcha: 'You were identified as a robot. If this is not the case, contact us by other means',
    },
    success: {
      title: 'Message received!',
      message: 'We will contact you soon',
    },
    validation: {
      phone: {
        min: 'Phone must have at least 7 digits',
        max: 'Phone must contain 20 digits maximum',
      },
    },
  },
};

const ContactFormTemplate: React.FC<ContactFormTemplateProps> = ({
  title,
  description,
  hideMessage,
  hidePhone,
  buttonText,
  loading,
  background,
  locale,
  error,
  success,
  destinationUrl,
  className,
  isCompany,
  onSubmit,
  urlOnNewTab,
  isLookingForJob,
  showTermsCheckBox: showTermsCheckBoxProp,
  successCardVariant = 'default',
  successMessageOverride,
  defaultValues: externalDefaultValues,
}) => {
  const [checked, setChecked] = React.useState(false);
  const [newsChecked, setNewsChecked] = React.useState(true);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      name: externalDefaultValues?.name || '',
      email: externalDefaultValues?.email || '',
      message: '',
      phone: externalDefaultValues?.phone || '',
      companySegment: '',
      role: '',
      companyName: '',
    },
  });
  const fields = watch();
  const isEn = locale === 'en';
  const showTermsCheckBoxDefault = React.useMemo(
    () =>
      fields.name &&
      fields.name !== '' &&
      fields.email &&
      fields.email !== '' &&
      (hideMessage || (fields.message && fields.message !== '')) &&
      (hidePhone || (fields.phone && fields.phone !== '')) &&
      (!isCompany || (fields.companyName && fields.role && fields.companySegment)),
    [fields],
  );

  const shouldShowTermsCheckBox =
    typeof showTermsCheckBoxProp === 'boolean' ? showTermsCheckBoxProp : showTermsCheckBoxDefault;

  const enableSubmitButton = React.useMemo(
    () =>
      fields.name &&
      fields.name !== '' &&
      fields.email &&
      fields.email !== '' &&
      (hideMessage || (fields.message && fields.message !== '')) &&
      (hidePhone || (fields.phone && fields.phone !== '')) &&
      (!isCompany || (fields.companyName && fields.role && fields.companySegment)),
    [fields, hideMessage, hidePhone, isCompany],
  );

  const text = texts[locale as 'pt' | 'en'];
  const successTitle = successMessageOverride || text.success.title;
  const successDescription = successMessageOverride ? undefined : text.success.message;

  React.useEffect(() => {
    const input = document.getElementById('phone-mask-input') as HTMLInputElement | null;
    if (input) {
      setValue('phone', input.value);
    }
  }, []);

  if (isLookingForJob)
    return (
      <ContactFormWrapper
        background={background as 'white' | 'primary'}
        className={className}
        inputError={!!errors.phone?.message}
        style={{ textAlign: 'left' }}
      >
        <LookingForJobText>
          <p>
            O único canal por onde contratamos desenvolvedores e estagiários de tecnologia é pela
            plataforma{' '}
            <a href="https://programador.emjuizdefora.com/" target="_blank" rel="noreferrer">
              Programadores em Juiz de Fora
            </a>
            .
            <br />
            <br />
            Não recebemos currículo e também não temos outro canal além deste.
            <br />
            <br />
            Tendo seu perfil (com e-mail confirmado) lá, quando novas vagas forem abertas você
            receberá um e-mail avisando para participar.
          </p>
        </LookingForJobText>
      </ContactFormWrapper>
    );

  return (
    <ContactFormWrapper
      success={success && (!destinationUrl || urlOnNewTab)}
      background={background as 'white' | 'primary'}
      className={className}
      inputError={!!errors.phone?.message}
    >
      {title && !success && <p className="contact-form-title">{title}</p>}
      {description && !success && <p>{description}</p>}
      <div className="content-wrapper">
        <CardsDisplay
          columns={1}
          cards={[
            {
              id: '1',
              title: successTitle,
              description: successDescription,
              image: <IoMdCheckmarkCircleOutline size={100} />,
            },
          ]}
          ghost
          imageIsComponent
          successCardAlternative={successCardVariant === 'alternative'}
          className={success && (!destinationUrl || urlOnNewTab) ? 'show' : ''}
        />
        <form
          name="budget-form"
          onSubmit={
            onSubmit &&
            handleSubmit((data) => {
              if (shouldShowTermsCheckBox && !checked) {
                setSubmitAttempted(true);
                return;
              }
              onSubmit(data as FormFields, newsChecked);
            })
          }
          className={success && (!destinationUrl || urlOnNewTab) ? 'hide' : ''}
          autoComplete="on"
        >
          <div className="form-row">
            <div className="input-label">
              <Input
                autoComplete="on"
                placeholder={text.name}
                type="text"
                {...register('name')}
                id="name"
                required
              />
              <label htmlFor="name" className="sr-only">
                {text.name}
              </label>
            </div>
            <div className="input-label">
              <Input
                placeholder={text.email}
                type="email"
                {...register('email')}
                id="email"
                required
              />
              <label htmlFor="email" className="sr-only">
                {text.email}
              </label>
              {((errors.email && errors.email.message) || error === 'email') && (
                <p className="error-message">
                  {errors.email?.message?.toString() ||
                    (isEn ? 'The email provided is not valid' : 'O email informado não é válido')}
                </p>
              )}
            </div>
            {!hidePhone && (
              <div className="input-label">
                {locale === 'pt' ? (
                  <Input
                    placeholder={text.phone}
                    type="tel"
                    value={fields.phone}
                    onChange={(e) => {
                      const formatted = formatPhoneBR(e.target.value);
                      setValue('phone', formatted);
                    }}
                    maxLength={15}
                  />
                ) : (
                  <Phone
                    country={locale === 'pt' ? 'br' : 'us'}
                    placeholder={text.phone}
                    inputProps={{ name: 'phone' }}
                    onChange={(value) => setValue('phone', value)}
                  />
                )}
                <input
                  type="tel"
                  hidden
                  {...register('phone', {
                    validate: (value) => {
                      if (!value) return true; // Campo não é obrigatório
                      const isValid = validatePhone(value, locale as 'pt' | 'en');
                      if (!isValid) {
                        return locale === 'pt'
                          ? 'Telefone inválido. Verifique o DDD e o número'
                          : 'Invalid phone number';
                      }
                      return true;
                    },
                  })}
                  id="phone-mask-input"
                  name=""
                />
                <label htmlFor="phone" className="sr-only">
                  {text.phone}
                </label>
                {errors.phone && errors.phone.message && (
                  <p className="error-message">{errors.phone.message}</p>
                )}
              </div>
            )}
          </div>
          {isCompany && (
            <div className="form-row">
              <div className="input-label">
                <Input
                  placeholder={text.companyName}
                  type="text"
                  {...register('companyName')}
                  id="companyName"
                  required
                />
                <label htmlFor="companyName" className="sr-only">
                  {text.companyName}
                </label>
              </div>
              <div className="input-label">
                <Input
                  placeholder={text.companySegment}
                  type="text"
                  {...register('companySegment')}
                  id="companySegment"
                  required
                />
                <label htmlFor="companySegment" className="sr-only">
                  {text.companySegment}
                </label>
              </div>
              <div className="input-label">
                <Input
                  placeholder={text.role}
                  type="text"
                  {...register('role')}
                  id="role"
                  required
                />
                <label htmlFor="role" className="sr-only">
                  {text.role}
                </label>
              </div>
            </div>
          )}
          {!hideMessage && (
            <div className="input-label">
              <TextArea
                placeholder={text.message}
                rows={4}
                {...register('message')}
                id="message"
                required
              />
              <label htmlFor="message" className="sr-only">
                {text.message}
              </label>
            </div>
          )}
          {shouldShowTermsCheckBox && (
            <>
              <div className="checkbox-wrapper" aria-live="assertive">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={newsChecked}
                    onChange={(e) => setNewsChecked(e.target.checked)}
                  />{' '}
                  <span className="checkmark" />
                  {isEn ? 'Keep me up to date' : 'Mantenha-me informado de novidades'}
                </label>
              </div>
              <div
                className={`checkbox-wrapper ${
                  submitAttempted && !checked ? 'shake-animation' : ''
                }`}
                aria-live="assertive"
              >
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      setChecked(e.target.checked);
                      if (e.target.checked) setSubmitAttempted(false);
                    }}
                  />{' '}
                  <span className="checkmark" />
                  {isEn
                    ? 'By ticking here, you are confirming that you have read and agreed with the '
                    : 'Confirmo que li e aceito os '}
                </label>
                {isEn ? (
                  <a
                    href="https://www.appmasters.io/en/terms-and-conditions"
                    target="_blank"
                    rel="noreferrer"
                  >
                    terms and conditions
                  </a>
                ) : (
                  <a href="https://www.appmasters.io/pt/termos-e-condicoes">termos e condições</a>
                )}
              </div>
            </>
          )}
          <footer>
            <Button
              backgroundColor={background}
              disabled={loading || !enableSubmitButton}
              type={!success ? 'submit' : 'button'}
            >
              {loading && <LoadingIcon />}
              {buttonText || text.send}
            </Button>
          </footer>

          {error === true && <FormErrorMessage id="error">{text.error.default}</FormErrorMessage>}
          {error === 'captcha' && (
            <FormErrorMessage id="error">{text.error.captcha}</FormErrorMessage>
          )}
        </form>
      </div>
    </ContactFormWrapper>
  );
};

export default ContactFormTemplate;
